<template>
  <UiSelect
    :items="items || []"
    :placeholder="$t('all_markets')"
    class="markets-select"
  >
    <template #top-label>
      <span class="light fw400 f12 p12 mb-2 db bg-dark">
        {{ $t('selected_markets_count', { n: selectedItems.length }) }}
        <span class="white">{{ $t(side).toLowerCase() }}</span>
      </span>
    </template>
    <template #top>
      <span
        v-if="selectedItems?.length === 1"
        class="f gap2"
      >
        <img
          :alt="selectedItems[0]"
          :src="getMarketImage(`${selectedItems[0]}.png`)"
          height="20"
          loading="lazy"
          width="20"
        >
        <span class="white short-span">
          {{ getMarketFullName(selectedItems[0]) }}
        </span>
      </span>
      <span
        v-if="selectedItems?.length > 1 && selectedItems?.length !== items?.length"
        class="f gap2"
      >
        <img
          v-for="item in selectedItems.slice(0, 3)"
          :key="item"
          :alt="item"
          :src="getMarketImage(`${item}.png`)"
          height="20"
          loading="lazy"
          width="20"
        >
        <span
          v-show="selectedItems?.length > 3"
          class="fw400"
        >
          +{{ selectedItems?.length - 3 }}
        </span>
      </span>
      <span v-if="selectedItems?.length === items?.length">
        <span>
          {{ $t('all_markets') }}
        </span>
      </span>
    </template>
    <template #item-top-in>
      <span
        class="f fs fg"
        @click.stop
      >
        <input
          :id="`all${uniqueId}`"
          v-model="selectAll"
          :has-child-checked="!!selectedItems.length"
          :name="uniqueId"
          class="primary-color"
          type="checkbox"
          @change="toggleSelectAll"
        >
        <label
          :for="`all${uniqueId}`"
          class="fg"
        >
          {{ $t('all_markets') }}
        </label>
      </span>
    </template>
    <template #default="{ item }">
      <span
        class="f fs btn"
        @click.stop
      >
        <input
          :id="getModifiedString(item.market_name) + uniqueId"
          v-model="model"
          :name="uniqueId"
          :value="item.market_name"
          class="primary-color"
          type="checkbox"
        >
        <label
          :for="getModifiedString(item.market_name) + uniqueId"
          class="fg"
        >
          <img
            :alt="item.logo_name"
            :src="getMarketImage(item.logo_name)"
            class="wh24 round mr8"
            height="24"
            loading="lazy"
            width="24"
          >
          {{ item.market_full_name }}
        </label>
      </span>
    </template>
  </UiSelect>
</template>

<script lang="ts" setup>
import type { AvailableMarkets } from '~/graphql/generated/schema-types';
import { getMarketImage } from '~/utils/get-image-url';

const props = defineProps<{
  items: AvailableMarkets[]
  selectedItems: string[]
  side: 'for_buying' | 'for_selling'
}>();
const model = defineModel();
const uniqueId = useId();

const selectAll = ref(false);

// Следим за изменениями `selectAll` и обновляем выбранные элементы
function toggleSelectAll() {
  if (selectAll.value) {
    // Если выбраны "Все рынки", добавляем все элементы в `model`
    model.value = props.items.map(item => item.market_name);
  } else {
    // Если снята галочка, очищаем `model`
    model.value = [];
  }
}

// Синхронизация `selectAll` с выбором всех элементов
watch(
  () => model.value,
  (newValue) => {
    selectAll.value = (newValue as any[]).length === props.items.length;
  },
);

function getMarketFullName(short: string) {
  return props.items.find(item => item.market_name === short)?.market_full_name;
}
</script>

<style scoped>
.markets-select {
  min-width: 125px;
  width: 100%;

  &:deep(.btn_close) {
    margin: 0;
  }
}

.bg-dark {
  background: var(--accent-on-primary);
}

.short-span {
  display: inline-block;
  max-width: 60px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
